import { formatPrice as formatPriceBase } from '@cvo/domain';
import type { Price } from '@cvo/domain';
import { computed, type ComputedRef, isRef, type MaybeRefOrGetter, toValue } from 'vue';
import { useI18n } from '@cvo/i18n/composables/useI18n';

type PriceFormatterOptions = Omit<Intl.NumberFormatOptions, 'style' | 'currency'>;

/**
 * Composable for formatting prices.
 *
 * This composable provides two usage patterns:
 * 1. Reactive formatting: Pass a price (ref/getter) to get a computed formatted value
 * 2. Formatter function: Pass only options to get a reusable formatter function
 *
 * The locale is automatically determined from the i18n context, with fallback
 * to a custom currency locale if the 'locale.currency' translation key exists.
 *
 * @example
 * // Reactive formatting
 * const price = ref({ amount: 100, currency: 'USD' })
 * const { formattedPrice } = usePriceFormatter(price)
 * // formattedPrice.value => "$100"
 *
 * @example
 * // Formatter function with custom options
 * const { formatPrice } = usePriceFormatter({ minimumFractionDigits: 0 })
 * formatPrice({ amount: 100, currency: 'EUR' }) // => "€100"
 *
 * @param price - Reactive or static price object containing amount and currency
 * @param options - Intl.NumberFormat options (excluding style and currency)
 * @returns Object with formattedPrice computed ref and formatPrice function
 */
export function usePriceFormatter(price: MaybeRefOrGetter<Price | null>, options?: PriceFormatterOptions): { formattedPrice: ComputedRef<string>; formatPrice: (price: Price) => string };

/**
 * Composable for formatting prices. Provides only a formatter function.
 *
 * @param options - Intl.NumberFormat options (excluding style and currency)
 * @returns Object with formatPrice function for manual formatting
 */
export function usePriceFormatter(options?: PriceFormatterOptions): { formatPrice: (price: Price) => string };

/**
 * Implementation of the usePriceFormatter composable.
 *
 * @param priceOrOptions - Either a Price object (reactive or static) or formatter options
 * @param options - Formatter options when the first argument is a Price
 * @returns Formatter function and optionally a computed formatted price
 */
export function usePriceFormatter(priceOrOptions?: MaybeRefOrGetter<Price | null> | PriceFormatterOptions, options?: PriceFormatterOptions) {
  // Check if first argument is a Price object
  function isPrice(value: unknown): value is MaybeRefOrGetter<Price> {
    const isRefOrGetter = isRef(value) || typeof value === 'function';
    if(isRefOrGetter) return true;

    const unwrapped = toValue(value);
    if (!unwrapped || typeof unwrapped !== 'object') return false;
    return 'amount' in unwrapped && 'currency' in unwrapped;
  }

  const hasPrice = isPrice(priceOrOptions);
  const formatterOptions = hasPrice ? options : (priceOrOptions as PriceFormatterOptions | undefined);

  const { t, te, locale } = useI18n();

  const currencyLocale = computed(() => {
    return te('custom.meta.currencyLocale') ? t('custom.meta.currencyLocale') : locale.value;
  });

  function formatPrice(priceValue: Price) {
    return formatPriceBase(priceValue.amount, priceValue.currency, currencyLocale.value, formatterOptions);
  }

  const formattedPrice = computed<string>(() => formatPrice(toValue(priceOrOptions as MaybeRefOrGetter<Price>)));

  if (hasPrice) {
    return {
      formattedPrice,
      formatPrice,
    };
  }

  return { formatPrice };
}
